import { createRef, createElement, Component } from 'react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

/**
 * A basic 3d vector used to track position and velocity of the balls
 */
var Vector = /** @class */ (function () {
    function Vector(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
    }
    Vector.prototype.setValue = function (x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
    };
    return Vector;
}());

/**
 * A point tracks the size and position of each ball
 */
var Point = /** @class */ (function () {
    function Point(x, y, z, size, color) {
        this.curPos = new Vector(x, y, z);
        this.size = size;
        this.color = color;
        this.friction = 0.8;
        this.originalPos = new Vector(x, y, z);
        this.radius = size;
        this.size = size;
        this.springStrength = 0.1;
        this.targetPos = new Vector(x, y, z);
        this.velocity = new Vector(0.0, 0.0, 0.0);
    }
    Point.prototype.update = function () {
        var dx = this.targetPos.x - this.curPos.x;
        var ax = dx * this.springStrength;
        this.velocity.x += ax;
        this.velocity.x *= this.friction;
        this.curPos.x += this.velocity.x;
        var dy = this.targetPos.y - this.curPos.y;
        var ay = dy * this.springStrength;
        this.velocity.y += ay;
        this.velocity.y *= this.friction;
        this.curPos.y += this.velocity.y;
        var dox = this.originalPos.x - this.curPos.x;
        var doy = this.originalPos.y - this.curPos.y;
        var dd = (dox * dox) + (doy * doy);
        var d = Math.sqrt(dd);
        this.targetPos.z = d / 100 + 1;
        var dz = this.targetPos.z - this.curPos.z;
        var az = dz * this.springStrength;
        this.velocity.z += az;
        this.velocity.z *= this.friction;
        this.curPos.z += this.velocity.z;
        this.radius = this.size * this.curPos.z;
        if (this.radius < 1) {
            this.radius = 1;
        }
    };
    Point.prototype.drawCanvas = function (cx) {
        cx.fillStyle = this.color;
        cx.beginPath();
        cx.arc(this.curPos.x, this.curPos.y, this.radius, 0, Math.PI * 2, true);
        cx.fill();
    };
    Point.prototype.drawDiv = function (parent) {
        if (!this.ballDiv) {
            this.ballDiv = document.createElement('div');
            this.ballDiv.classList.add('point');
            parent.appendChild(this.ballDiv);
        }
        this.ballDiv.setAttribute('style', "background-color: " + this.color + ";\n      top: " + (this.curPos.y - this.radius) + "px;\n      left: " + (this.curPos.x - this.radius) + "px;\n      width: " + this.radius * 2 + "px;\n      height: " + this.radius * 2 + "px");
    };
    return Point;
}());

/**
 * A collection of points / balls to be rendered to either a canvas or the dom as well as utilities to populate it
 */
var PointCollection = /** @class */ (function () {
    function PointCollection() {
        this.mousePos = new Vector(0, 0, 0);
        this.points = [];
    }
    /**
     * Generates a css style color from the RGBA coordinates of a color
     */
    PointCollection.toColor = function (r, g, b, a) {
        return 'rgba(' + [r, g, b, a].join(',') + ')';
    };
    /**
     * Adjusts a point to fit within the desired area
     */
    PointCollection.scalePoint = function (p, canvasWidth, canvasHeight, imageWidth, imageHeight) {
        p.curPos.x = ((canvasWidth - imageWidth) / 2) + p.curPos.x;
        p.curPos.y = ((canvasHeight - imageHeight) / 2) + p.curPos.y;
        p.originalPos.x = ((canvasWidth - imageWidth) / 2) + p.originalPos.x;
        p.originalPos.y = ((canvasHeight - imageHeight) / 2) + p.originalPos.y;
    };
    /**
     * Determines the average color within the cell so we know the color of the ball, summing the squares gives a better looking color
     */
    PointCollection.dominantColor = function (x, y, w, cellSize, data) {
        // Average the colors in the area
        var pixels = 0;
        var rSummed = 0;
        var gSummed = 0;
        var bSummed = 0;
        var aSummed = 0;
        for (var localX = x; localX < x + cellSize; localX++) {
            for (var localY = y; localY < y + cellSize; localY++) {
                var i = ((y * w) + x) * 4;
                if (i < data.byteLength) {
                    var r = data[i];
                    rSummed += r * r;
                    var g = data[i + 1];
                    gSummed += g * g;
                    var b = data[i + 2];
                    bSummed += b * b;
                    var a = data[i + 3];
                    aSummed += a * a;
                    pixels++;
                }
            }
        }
        var rFinal = Math.sqrt(rSummed / pixels);
        var gFinal = Math.sqrt(gSummed / pixels);
        var bFinal = Math.sqrt(bSummed / pixels);
        var aFinal = Math.sqrt(aSummed / pixels);
        if (aFinal < 10 || (rFinal > 230 && gFinal > 230 && bFinal > 230)) {
            return null;
        }
        return PointCollection.toColor(rFinal, gFinal, bFinal, aFinal);
    };
    /**
     * Extracts point for an image into the point collection
     */
    PointCollection.prototype.loadFromSource = function (src, destWidth, destHeight, cellSize) {
        var _this = this;
        var img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function () {
            var tempCanvas = document.createElement('canvas');
            tempCanvas.className = 'ng-bouncing-balls-bg-canvas';
            tempCanvas.width = img.width;
            tempCanvas.height = img.height;
            var context = tempCanvas.getContext('2d');
            if (context != null) {
                context.drawImage(img, 0, 0, img.width, img.height);
                var pixels = context.getImageData(0, 0, img.width, img.height);
                _this.pointsFromImage(pixels, destWidth, destHeight, cellSize);
            }
        };
        img.onerror = function (err) {
            console.log(err);
        };
        img.src = src;
    };
    PointCollection.prototype.pointsFromImage = function (img, destWidth, destHeight, cellSize) {
        var realCellSize = Math.max(1, cellSize);
        var cellRadius = Math.floor(realCellSize / 2);
        var cols = Math.ceil(img.width / realCellSize);
        var rows = Math.ceil(img.height / realCellSize);
        for (var y = 0; y < rows; y++) {
            for (var x = 0; x < cols; x++) {
                var cellX = x * realCellSize;
                var cellY = y * realCellSize;
                var color = PointCollection.dominantColor(cellX, cellY, img.width, realCellSize, img.data);
                if (color) {
                    var p = new Point(cellX + cellRadius, cellY + cellRadius, 0, cellRadius, color);
                    PointCollection.scalePoint(p, destWidth, destHeight, img.width, img.height);
                    this.points.push(p);
                }
            }
        }
    };
    /**
     * Triggers the per-frame updates to the point collection
     */
    PointCollection.prototype.update = function () {
        for (var _i = 0, _a = this.points; _i < _a.length; _i++) {
            var point = _a[_i];
            var dx = this.mousePos.x - point.curPos.x;
            var dy = this.mousePos.y - point.curPos.y;
            var dd = (dx * dx) + (dy * dy);
            var d = Math.sqrt(dd);
            if (d < 150) {
                point.targetPos.x = (this.mousePos.x < point.curPos.x) ? point.curPos.x - dx : point.curPos.x - dx;
                point.targetPos.y = (this.mousePos.y < point.curPos.y) ? point.curPos.y - dy : point.curPos.y - dy;
            }
            else {
                point.targetPos.x = point.originalPos.x;
                point.targetPos.y = point.originalPos.y;
            }
            point.update();
        }
    };
    /**
     * Draws the point collection to a canvas
     */
    PointCollection.prototype.drawCanvas = function (cx) {
        for (var _i = 0, _a = this.points; _i < _a.length; _i++) {
            var point = _a[_i];
            point.drawCanvas(cx);
        }
    };
    /**
     * Draws the point collection to the DOM
     */
    PointCollection.prototype.drawDiv = function (parent) {
        for (var _i = 0, _a = this.points; _i < _a.length; _i++) {
            var point = _a[_i];
            point.drawDiv(parent);
        }
    };
    return PointCollection;
}());

var BouncingBallsCanvasComponent = /** @class */ (function (_super) {
    __extends(BouncingBallsCanvasComponent, _super);
    function BouncingBallsCanvasComponent(props) {
        var _this = _super.call(this, props) || this;
        _this.pointCollection = new PointCollection();
        _this.canvasRef = createRef();
        return _this;
    }
    BouncingBallsCanvasComponent.prototype.render = function () {
        return (createElement("canvas", { ref: this.canvasRef, className: "react-bouncing-balls-canvas" }));
    };
    BouncingBallsCanvasComponent.prototype.componentDidMount = function () {
        var _this = this;
        var canvasEl = this.canvasRef.current;
        if (!canvasEl) {
            return;
        }
        canvasEl.ontouchstart = function (e) {
            e.preventDefault();
        };
        canvasEl.ontouchmove = function (e) {
            e.preventDefault();
            var mPosx = 0;
            var mPosy = 0;
            var ePosx = 0;
            var ePosy = 0;
            if (e.targetTouches[0].pageX || e.targetTouches[0].pageY) {
                mPosx = e.targetTouches[0].pageX;
                mPosy = e.targetTouches[0].pageY;
            }
            else if (e.targetTouches[0].clientX || e.targetTouches[0].clientY) {
                mPosx = e.targetTouches[0].clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
                mPosy = e.targetTouches[0].clientY + document.body.scrollTop + document.documentElement.scrollTop;
            }
            var currentObject = canvasEl;
            if (currentObject.offsetParent) {
                do {
                    ePosx += currentObject.offsetLeft;
                    ePosy += currentObject.offsetTop;
                    currentObject = currentObject.offsetParent;
                } while (currentObject.offsetParent);
            }
            _this.pointCollection.mousePos.setValue(mPosx - ePosx, mPosy - ePosy, 0);
        };
        canvasEl.ontouchend = function (e) {
            e.preventDefault();
            _this.pointCollection.mousePos.setValue(-999, -999, -999);
        };
        canvasEl.ontouchcancel = function (e) {
            e.preventDefault();
            _this.pointCollection.mousePos.setValue(-999, -999, -999);
        };
        canvasEl.onmousemove = function (e) {
            var mPosx = 0;
            var mPosy = 0;
            var ePosx = 0;
            var ePosy = 0;
            if (e.pageX || e.pageY) {
                mPosx = e.pageX;
                mPosy = e.pageY;
            }
            else if (e.clientX || e.clientY) {
                mPosx = e.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
                mPosy = e.clientY + document.body.scrollTop + document.documentElement.scrollTop;
            }
            var currentObject = canvasEl;
            if (currentObject.offsetParent) {
                do {
                    ePosx += currentObject.offsetLeft;
                    ePosy += currentObject.offsetTop;
                    currentObject = currentObject.offsetParent;
                } while (currentObject.offsetParent);
            }
            _this.pointCollection.mousePos.setValue(mPosx - ePosx, mPosy - ePosy, 0);
        };
        canvasEl.onmouseleave = function (_e) {
            _this.pointCollection.mousePos.setValue(-999, -999, -999);
        };
        this.cx = canvasEl.getContext('2d');
        // set the width and height
        canvasEl.width = this.props.width;
        canvasEl.height = this.props.height;
        this.pointCollection.loadFromSource(this.props.src, this.props.width, this.props.height, this.props.cellSize);
        this.timeout();
    };
    BouncingBallsCanvasComponent.prototype.timeout = function () {
        var _this = this;
        var canvasEl = this.canvasRef.current;
        if (!canvasEl || !this.cx) {
            return;
        }
        this.cx.clearRect(0, 0, canvasEl.width, canvasEl.height);
        this.pointCollection.drawCanvas(this.cx);
        this.pointCollection.update();
        setTimeout(function () { return _this.timeout(); }, 30);
    };
    BouncingBallsCanvasComponent.defaultProps = {
        src: '',
        width: 512,
        height: 512,
        cellSize: 20
    };
    return BouncingBallsCanvasComponent;
}(Component));

var BouncingBallsDivComponent = /** @class */ (function (_super) {
    __extends(BouncingBallsDivComponent, _super);
    function BouncingBallsDivComponent(props) {
        var _this = _super.call(this, props) || this;
        _this.pointCollection = new PointCollection();
        _this.containerRef = createRef();
        return _this;
    }
    BouncingBallsDivComponent.prototype.render = function () {
        return (createElement("div", { ref: this.containerRef, className: "BouncingBallsDiv_container" }));
    };
    BouncingBallsDivComponent.prototype.componentDidMount = function () {
        var _this = this;
        var containerEl = this.containerRef.current;
        if (!containerEl) {
            return;
        }
        containerEl.ontouchstart = function (e) {
            e.preventDefault();
        };
        containerEl.ontouchmove = function (e) {
            e.preventDefault();
            var mPosx = 0;
            var mPosy = 0;
            var ePosx = 0;
            var ePosy = 0;
            if (e.targetTouches[0].pageX || e.targetTouches[0].pageY) {
                mPosx = e.targetTouches[0].pageX;
                mPosy = e.targetTouches[0].pageY;
            }
            else if (e.targetTouches[0].clientX || e.targetTouches[0].clientY) {
                mPosx = e.targetTouches[0].clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
                mPosy = e.targetTouches[0].clientY + document.body.scrollTop + document.documentElement.scrollTop;
            }
            var currentObject = containerEl;
            if (currentObject.offsetParent) {
                do {
                    ePosx += currentObject.offsetLeft;
                    ePosy += currentObject.offsetTop;
                    currentObject = currentObject.offsetParent;
                } while (currentObject.offsetParent);
            }
            _this.pointCollection.mousePos.setValue(mPosx - ePosx, mPosy - ePosy, 0);
        };
        containerEl.ontouchend = function (e) {
            e.preventDefault();
            _this.pointCollection.mousePos.setValue(-999, -999, -999);
        };
        containerEl.ontouchcancel = function (e) {
            e.preventDefault();
            _this.pointCollection.mousePos.setValue(-999, -999, -999);
        };
        containerEl.onmousemove = function (e) {
            var mPosx = 0;
            var mPosy = 0;
            var ePosx = 0;
            var ePosy = 0;
            if (e.pageX || e.pageY) {
                mPosx = e.pageX;
                mPosy = e.pageY;
            }
            else if (e.clientX || e.clientY) {
                mPosx = e.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
                mPosy = e.clientY + document.body.scrollTop + document.documentElement.scrollTop;
            }
            var currentObject = containerEl;
            if (currentObject.offsetParent) {
                do {
                    ePosx += currentObject.offsetLeft;
                    ePosy += currentObject.offsetTop;
                    currentObject = currentObject.offsetParent;
                } while (currentObject.offsetParent);
            }
            _this.pointCollection.mousePos.setValue(mPosx - ePosx, mPosy - ePosy, 0);
        };
        containerEl.onmouseleave = function (_e) {
            _this.pointCollection.mousePos.setValue(-999, -999, -999);
        };
        containerEl.setAttribute('style', "width:" + this.props.width + "px; height:" + this.props.height + "px");
        this.pointCollection.loadFromSource(this.props.src, this.props.width, this.props.height, this.props.cellSize);
        this.timeout();
    };
    BouncingBallsDivComponent.prototype.timeout = function () {
        var _this = this;
        var container = this.containerRef.current;
        if (!container) {
            return;
        }
        this.pointCollection.drawDiv(container);
        this.pointCollection.update();
        setTimeout(function () { return _this.timeout(); }, 30);
    };
    BouncingBallsDivComponent.defaultProps = {
        src: '',
        width: 512,
        height: 512,
        cellSize: 20
    };
    return BouncingBallsDivComponent;
}(Component));

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css = " .BouncingBallsDiv_container {\r\n    position: relative;\r\n  }\r\n\r\n.BouncingBallsDiv_container > .point {\r\n    position: absolute;\r\n    border-radius: 50%;\r\n  }\r\n";
styleInject(css);

var index$1 = { BouncingBallsDivComponent: BouncingBallsDivComponent, BouncingBallsCanvasComponent: BouncingBallsCanvasComponent };

export default index$1;
export { BouncingBallsDivComponent, BouncingBallsCanvasComponent };
//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ../dist/index.es.js
// module id = 18
// module chunks = 0